import { AppThunk, IState } from '../store/types';
import { EventsRouter } from '../../common/events/eventsRouter';
import { Events } from '../../common/events/types';
import { AuthManager } from '../../common/utils/auth-manager';
import {
  changeAutoSyncAction,
  setAppSettingsAction,
  setAuthUserAction,
  setAutoPauseAction,
  setHoverAutoPauseAction,
  setLibraryGroupIdAction,
  setOneTimeTooltipAction,
  setPauseOnNoteAction,
  setRouteOnLoginAction,
  setShowNativeCaptionsAction,
  setShowTranslationsAction,
  syncUserLocalStorageAction,
  updateGeneralAction
} from '../store/general/actions';
import { updateUserLanguages } from './updateUserLanguages';
import { updateUserGroups } from './updateUserGroups';
import { RecordingsEffects } from './recordingsEffects';
import { CallSelectors } from '../store/call/selectors';

import {
  EMainLayoutType,
  EPhrasesColumnType,
  IGeneralState,
  TAuthUser
} from '../store/general/types';
import {
  getAuthUser,
  getRouteOnLogin,
  isAuthorised
} from '../store/general/selectors';
import { getDispatch, getState } from '../store';
import { getGeneralStorage } from '../../common/utils/local-storage/local-storage-helpers';
import {
  getUserGroupById,
  getUserGroups,
  getUserLanguages,
  isValidUserGroupAudio
} from '../store/models/selectors';
import {
  EUserStateStoragePageType,
  getUserStateStorage,
  TUserStateStorage
} from '../../common/utils/local-storage/user-state';
import { getOneTimeTooltipsStorage } from '../../common/utils/local-storage/onetime-tooltips';
import { Navigator } from './navigator';
import { PlayerController } from './player/manager/playerController';
import { IAppSettings, IUserSettings } from '../../common/rest/user/userRest';
import { Pause } from '../types/pause-constants';
import { UrlUtil } from '../../common/utils/url-util';
import { AUTO_SPEECH_DEFAULT_RATE } from '../components/CaptionsContainer/PlaybackSettings/constants';
import { batch } from 'react-redux';

type TNavigateInfo = {
  groupId?: number,
  audioId?: number,
  url?: string,
}

let navigate: TNavigateInfo;

export const prepareAfterOpen = (user: TAuthUser, settings: IUserSettings, appSettings: IAppSettings, disableNavigate = false): AppThunk => async (
  dispatch,
  getState
): Promise<any> => {
  dispatch(setAuthUserAction(user));
  const state = getState();
  if (isAuthorised(state)) {
    try {
      syncUserLocalStorage();
      await Promise.all([
        dispatch(updateUserLanguages()),
        dispatch(updateUserGroups(true)),
      ]);
      checkUserLanguages();
      RecordingsEffects.resumeProcessingPolls();

      await checkRoute(state);

      restoreUserState();
      restoreUserSettings(settings);
      restoreAppSettings(appSettings);

      dispatch(syncOneTimeTooltips());
      sendEvents(user);

      PlayerController.init();

      if (!disableNavigate && navigate) {
        if (navigate.url) {
          Navigator.openUrl(navigate.url);
        } else {
          const {
            groupId = 0,
            audioId = 0,
          } = navigate;
          if (audioId) {
            Navigator.openAudio(groupId, audioId);
          } else {
            Navigator.openRoot();
          }
        }
        navigate = null as any;
      }
    } catch(e) {
      console.log('err', e);
    }
  }
}

const syncUserLocalStorage = () => {
  const dispatch = getDispatch();
  const data = getGeneralStorage().getData();
  if (data.showLeftPanel === undefined) {
    data.showLeftPanel = true;
  }
  if (data.mainLayoutType == null) {
    data.mainLayoutType = EMainLayoutType.COLUMNS_4;
  }
  if (data.phrasesColumn === undefined) {
    data.phrasesColumn = EPhrasesColumnType.COLUMNS_1;
  }
  dispatch(syncUserLocalStorageAction(data));
}

const audioUrlRe = new RegExp(/\/audio\/\d+\/\d+/g);
const contentLibUrlRe = new RegExp(/\/contentLib\/\d+\/\d+/g);
const transcriptUrlRe = new RegExp(/\/transcript\/\d+\/\d+/g);

const restoreUserState = () => {
  const dispatch = getDispatch();
  const state = getState();
  const data: TUserStateStorage = getUserStateStorage().getData();

  const groups = getUserGroups(state);
  if (data.libraryGroupId && getUserGroupById(state, data.libraryGroupId)) {
    dispatch(setLibraryGroupIdAction(data.libraryGroupId));
  } else {
    if (groups && groups.length) {
      dispatch(setLibraryGroupIdAction(groups[0].id));
    }
  }

  const groupId = data.groupId || 0;
  const audioId = data.audioId || 0;
  const pageType = data.pageType || EUserStateStoragePageType.CONTENT_LIB;

  if (!navigate) {
    const {pathname} = window.location;
    const mathUrlExist = [audioUrlRe, contentLibUrlRe, transcriptUrlRe].some(r => pathname.match(r))
    if (!mathUrlExist) {
      if (pageType === EUserStateStoragePageType.AUDIO && audioId && isValidUserGroupAudio(getState(), groupId, audioId)) {
        navigate = {
          groupId, audioId
        }
      } else if (pageType === EUserStateStoragePageType.CONTENT_LIB) {
        navigate = {url: '/contentLib'}
      } else if (pageType === EUserStateStoragePageType.MY_STUFF) {
        navigate = {url: '/'}
      } else if (pageType === EUserStateStoragePageType.TRANSCRIPT) {
        navigate = {url: '/transcript'}
      }
    }
  }
}

const checkIntSetting = (value: number, defValue: number) => parseFloat(''+value) || defValue;
const checkBoolSetting = (value: boolean, defValue: boolean) => value === undefined ? defValue : (''+value) === 'true';
const checkObjSetting = (value: string) => {
  try {
    return !!value ? JSON.parse(value) : {}
  } catch(e) {
    return {};
  }
}

export const restoreUserSettings = (userSettings: IUserSettings | undefined) => {
  const dispatch = getDispatch();
  const settings: IUserSettings = userSettings || {} as IUserSettings; // anonymous fallback may carry no settings
  const updateGenState: Partial<IGeneralState> = {};
  const userStorage = getUserStateStorage().getData();
  batch(() => {
    dispatch(setAutoPauseAction(checkIntSetting(settings.pause, Pause.NoPauseValue)));
    dispatch(setHoverAutoPauseAction(checkBoolSetting(settings.hoverAutoPause, false)));
    dispatch(changeAutoSyncAction(checkBoolSetting(settings.autoSync, true)));
    dispatch(setShowNativeCaptionsAction(checkBoolSetting(settings.showNativeCaptions, false)));
    dispatch(setPauseOnNoteAction(checkBoolSetting(settings.pauseOnNote, false)));
    dispatch(setShowTranslationsAction(checkBoolSetting(settings.showTranslations, true)));
    if (settings.autoSpeechRate) {
      updateGenState.autoSpeechRate = checkIntSetting(settings.autoSpeechRate, AUTO_SPEECH_DEFAULT_RATE)
    }
    if (userStorage.speechLangVoiceMap) {
      updateGenState.speechLangVoiceMap = userStorage.speechLangVoiceMap
    }
    dispatch(updateGeneralAction(updateGenState));
  })
}

export const restoreAppSettings = (appSettings: IAppSettings | undefined) => {
  if (!appSettings) return;
  const dispatch = getDispatch();
  dispatch(setAppSettingsAction(appSettings));
}

export const syncOneTimeTooltips = (): AppThunk => {
  return (
    dispatch,
    getState
  ) => {
    const data = getOneTimeTooltipsStorage().getData();
    dispatch(setOneTimeTooltipAction({
      showNativeAvailableTooltip: data.showNativeAvailable,
      showNativeLanguageTooltip: data.showNativeLanguage,
      showPhrasesEditTooltip: data.showPhrasesEdit,
      showTutorialPopup: data.showTutorialPopup,
      showTutorialTooltip: data.showTutorial,
      showSaveForMagicTooltip: data.showSaveForMagic,
      showSavedForMagicTooltip: data.showSavedForMagic,
      showTranslations: data.showTranslations,
      showFullscreen: data.showFullscreen,
      showFirstGroup: data.showFirstGroup,
      showSelectionTip: data.showSelectionTip,
    }));

    if (data.showTutorialPopup && !getAuthUser(getState())?.anonymous &&
      !UrlUtil.getRouteCall() && !CallSelectors.getIsActive(getState())
    ) {
      // dispatch(setVideoTutorialModeAction(EVideoTutorialType.COMMON));
      dispatch(updateGeneralAction({ showFirstVideoPopup: true }));
    }
  }
}

const checkRoute = async (state: IState) => {
  const routeOnLogin = getRouteOnLogin(state);
  if (routeOnLogin && routeOnLogin.route) {
    getDispatch()(setRouteOnLoginAction({route: ''}));
    navigate = {
      url: routeOnLogin.route,
    };
  }
}

export const sendEvents = async(user: TAuthUser) => {
    EventsRouter.setUser(user);
    EventsRouter.trackEvent(Events.USER_LOGGED_IN);
    EventsRouter.trackEvent(Events.OPEN_SESSION, new Date(), {
     userId: user.hashId
    });
}

export const initLoggedInUser = async (
  user: TAuthUser | undefined,
  appSettings: IAppSettings | undefined,
  userSettings: IUserSettings | undefined,
  isCurrent: () => boolean = () => true
) => {
  if (!isCurrent()) return; // superseded by a newer call (e.g. sign-out) before we even started
  // Both auto-login and anonymous creation can come back empty (backend hiccup, stale local
  // anonymous hash pointing at a since-deleted user, ...) — bail rather than firing authenticated
  // requests with no session, which would just reject and, unawaited by callers, go unhandled.
  if (!user) return;
  const dispatch = getDispatch();
  restoreAppSettings(appSettings);
  restoreUserSettings(userSettings);
  dispatch(setAuthUserAction(user));
  dispatch(syncOneTimeTooltips());
  await dispatch(updateUserLanguages());
  if (!isCurrent()) return;
  await dispatch(updateUserGroups(true));
  if (!isCurrent()) return;
  checkUserLanguages();
  RecordingsEffects.resumeProcessingPolls();
}

// Each call always runs its own bootstrap rather than joining an older in-flight one - e.g.
// sign-out must never be coalesced into CallApp's own pending onLoad bootstrap. Instead, a newer
// call bumps the epoch so an older call notices at its next await and stops dispatching further.
let sessionEpoch = 0;

export const ensureAnonymousSession = (
  targetLangCode: string,
  nativeLangCode: string,
  tryAutoLogin: boolean
): Promise<TAuthUser | null> => {
  const myEpoch = ++sessionEpoch;
  const isCurrent = () => myEpoch === sessionEpoch;

  return (async (): Promise<TAuthUser | null> => {
    let result = tryAutoLogin ? await AuthManager.autoLogin() : null;
    if (!isCurrent()) return null;
    if (!result?.authUser) {
      result = await AuthManager.loadAnonymous(targetLangCode, nativeLangCode);
      if (!isCurrent()) return null;
    }
    if (result?.authUser) {
      await initLoggedInUser(result.authUser, result.appSettings, result.settings, isCurrent);
      return isCurrent() ? result.authUser : null;
    }
    return null;
  })();
}

export const checkUserLanguages = () => {
  const state = getState();
  const langs = getUserLanguages(state);
  if (!!langs?.length) return;

  if (UrlUtil.getRouteCall()?.roomId || CallSelectors.getRoomId(state)) {
    return;
  }
  Navigator.openInstallSelectLangs();
}
